import { createRouter, Link } from "@tanstack/react-router";
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query";
import { SearchX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { env } from "@/env";
import * as TanstackQuery from "./integrations/tanstack-query/root-provider";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";

// 404 Not Found Component
function NotFound() {
	return (
		<div className="flex min-h-screen items-center justify-center px-4">
			<div className="mx-auto max-w-md space-y-6 text-center">
				<SearchX className="mx-auto size-16 text-muted-foreground" />
				<div className="space-y-2">
					<h1 className="text-2xl font-semibold tracking-tight">Page Not Found</h1>
					<p className="text-muted-foreground">
						The page you're looking for doesn't exist or has been moved.
					</p>
				</div>
				<Button asChild>
					<Link to="/">Back to Dashboard</Link>
				</Button>
			</div>
		</div>
	);
}

// Create a new router instance
export const getRouter = () => {
	const rqContext = TanstackQuery.getContext();

	const router = createRouter({
		routeTree,
		context: { ...rqContext },
		defaultPreload: "intent",
		defaultNotFoundComponent: NotFound,
		notFoundMode: "root",
		Wrap: (props: { children: React.ReactNode }) => {
			return <TanstackQuery.Provider {...rqContext}>{props.children}</TanstackQuery.Provider>;
		},
	});

	// Initialize Sentry lazily on client-side only — keeps ~50KB out of the initial bundle
	if (!router.isServer && env.VITE_SENTRY_DSN) {
		import("@sentry/tanstackstart-react").then((Sentry) => {
			Sentry.init({
				dsn: env.VITE_SENTRY_DSN,
				tunnel: `${env.VITE_API_BASE_URL}/api/v1/sentry-tunnel`,
				environment: import.meta.env.DEV ? "development" : "production",
				integrations: [
					Sentry.tanstackRouterBrowserTracingIntegration(router),
					Sentry.replayIntegration(),
				],
				tracesSampleRate: 1.0,
				replaysOnErrorSampleRate: 1.0, // capture session replay for every error
				beforeSend(event) {
					try {
						const onboarding = localStorage.getItem("onboarding");
						if (onboarding) {
							event.contexts = {
								...event.contexts,
								localStorage: { onboarding },
							};
						}
					} catch {
						// localStorage may be unavailable
					}
					return event;
				},
			});
		});
	}

	setupRouterSsrQueryIntegration({
		router,
		queryClient: rqContext.queryClient,
	});

	return router;
};
